import { plNumber } from '../../shared/number';

/**
 * Kilometry dolotu i trasy zlecenia z podpisanym źródłem (06.10.2026).
 *
 * Lustro typu `RouteFigure` z API. Decyzja użytkownika z 06.10.2026: dolot
 * i trasę zlecenia liczymy mapami HERE (AWS Location, profil ciężarówki),
 * a dotychczasowy rachunek — linia prosta razy mnożnik terenowy, czas
 * z modelu floty — zostaje jako poglądowy, w dymku obok liczby („te
 * obliczenia, które teraz mamy, zostaw jako poglądowe w popupie zaraz obok”).
 *
 * `source` mówi, skąd jest główna liczba: `here` — trasa z map, `tms` —
 * dystans zapisany w TMS (tylko trasa zlecenia), `estimate` — nasz szacunek,
 * gdy HERE jest wyłączone albo nie odpowiedziało (powód w `note`). Starsze
 * API pola nie ma wcale — wtedy ekran wygląda jak dotąd (`accessDistance`).
 */
export type RouteFigureSource = 'here' | 'tms' | 'estimate';

export interface RouteFigureEstimate {
  straightKm: number | null;
  multiplier: number | null;
  km: number | null;
  tmsKm: number | null;
  driveHours: number | null;
}

/**
 * Opłaty drogowe z HERE (decyzja użytkownika 06.10.2026: doliczamy je do
 * kosztu i marży). `pln` w złotych po przeliczeniu, `amount` i `currency`
 * — kwota, jak ją podały mapy. `null` przy `source = 'here'` = mapy nie
 * podały opłat (brak danych, nie zero).
 */
export interface RouteTolls {
  pln: number | null;
  amount: number | null;
  currency: string | null;
  note: string | null;
}

export interface RouteFigure {
  source: RouteFigureSource;
  /** Główna liczba kilometrów. */
  km: number | null;
  /** Czas samej jazdy, bez przerw. */
  driveHours: number | null;
  estimate: RouteFigureEstimate;
  /** Po polsku z API: powód szacunku albo profil ciężarówki. */
  note: string | null;
  /** Opłaty drogowe; brak pola = API sprzed opłat (bez dopisku). */
  tolls?: RouteTolls | null;
}

/** Podpis źródła pod liczbą. */
export const ROUTE_SOURCE_LABELS: Record<RouteFigureSource, string> = {
  here: 'HERE',
  tms: 'TMS',
  estimate: 'szacunek',
};

/** Liczba z map albo z TMS — wszystko poza naszym szacunkiem. */
export const isMeasured = (f: RouteFigure | null | undefined): boolean =>
  !!f && f.source !== 'estimate';

/** „329,4 km” albo „—”, gdy liczby nie ma (brak danych, nie zero). */
export const kmText = (km: number | null): string => (km === null ? '—' : `${plNumber(km)} km`);

/**
 * Czas jazdy pod liczbą z map: „jazda ok. 4,3 h”. Tylko przy trasie z map
 * albo z TMS — przy szacunku czas z modelu floty stoi w dymku, jak dotąd.
 */
export function driveText(f: RouteFigure): string | null {
  if (!isMeasured(f) || f.driveHours === null) return null;
  return `jazda ok. ${plNumber(f.driveHours)} h`;
}

/**
 * Rachunek szacunku w jednym zdaniu: „linia prosta 252,4 km × mnożnik
 * terenowy 1,305 = 329,4 km; czas jazdy wg modelu floty ok. 4,6 h”.
 * Dystans z TMS dopisujemy, gdy nie jest już główną liczbą.
 */
export function estimateBreakdown(f: RouteFigure): string {
  const e = f.estimate;
  const parts: string[] = [];
  if (e.straightKm !== null && e.multiplier !== null) {
    const product = e.km !== null ? ` = ${plNumber(e.km)} km` : '';
    parts.push(
      `linia prosta ${plNumber(e.straightKm)} km × mnożnik terenowy ${plNumber(e.multiplier, 3)}${product}`,
    );
  } else if (e.km !== null) {
    parts.push(`${plNumber(e.km)} km`);
  }
  if (e.driveHours !== null) parts.push(`czas jazdy wg modelu floty ok. ${plNumber(e.driveHours)} h`);
  if (e.tmsKm !== null && f.source !== 'tms') parts.push(`dystans z TMS: ${plNumber(e.tmsKm)} km`);
  return parts.join('; ');
}

/**
 * Treść dymka obok liczby. Przy liczbie z map — szacunek jako poglądowy
 * („Szacunek poglądowy: …”); przy samym szacunku — jego rachunek. Na końcu
 * uwaga z API (powód szacunku, profil ciężarówki).
 */
export function routeFigureTooltip(f: RouteFigure): string {
  const breakdown = estimateBreakdown(f);
  const head = isMeasured(f) ? 'Szacunek poglądowy' : 'Szacunek';
  const sentences = [breakdown ? `${head}: ${breakdown}.` : `${head}: brak danych do rachunku.`];
  if (f.note) {
    // Uwaga z API jako osobne zdanie — wielką literą, z kropką.
    const note = `${f.note.charAt(0).toUpperCase()}${f.note.slice(1)}`;
    sentences.push(note.endsWith('.') ? note : `${note}.`);
  }
  return sentences.join(' ');
}

/**
 * Trasa zlecenia jednym zwrotem: „812 km wg HERE, jazda ok. 10,1 h”,
 * „640 km wg TMS”, „ok. 655 km (szacunek)”.
 */
export function routeSummaryText(f: RouteFigure): string {
  if (!isMeasured(f)) return f.km === null ? 'brak danych' : `ok. ${kmText(f.km)} (szacunek)`;
  const drive = driveText(f);
  return `${kmText(f.km)} wg ${ROUTE_SOURCE_LABELS[f.source]}${drive ? `, ${drive}` : ''}`;
}

const zl = (v: number) => `${v.toLocaleString('pl-PL')} zł`;

/**
 * Opłaty drogowe jednym zwrotem — do dymka kosztu dolotu i marży:
 * „opłaty drogowe ~85 zł (20 EUR, wg HERE)”, przy trasie z map bez opłat
 * od HERE — „opłaty drogowe: brak danych”, przy trasie bez map — „bez opłat
 * drogowych”. `null` — nie ma czego dopisać (starsze API bez pola `tolls`
 * albo bez liczby dolotu w ogóle). `where` — „na dolocie”, „na trasie
 * zlecenia”, gdy w jednym dymku są obie trasy.
 */
export function tollsText(f: RouteFigure | null | undefined, where?: string): string | null {
  if (!f || f.tolls === undefined) return null;
  const subject = where ? `opłaty drogowe ${where}` : 'opłaty drogowe';
  if (f.source !== 'here') return where ? `${where} bez opłat drogowych` : 'bez opłat drogowych';
  const t = f.tolls;
  if (t === null) return `${subject}: brak danych`;
  const inCurrency =
    t.amount !== null && t.currency !== null && t.currency !== 'PLN'
      ? `${plNumber(t.amount, 2)} ${t.currency}, `
      : '';
  const note = t.note ? `; ${t.note}` : '';
  if (t.pln !== null) return `${subject} ~${zl(t.pln)} (${inCurrency}wg HERE)${note}`;
  if (t.amount !== null && t.currency !== null) {
    return `${subject} ${plNumber(t.amount, 2)} ${t.currency} (wg HERE), bez przeliczenia na złote${note}`;
  }
  return `${subject}: brak danych${note}`;
}

/**
 * Zdania o opłatach do dymka — dla każdej trasy po jednym; gdy rachunek
 * z API już je wymienia (`breakdown`), nie powtarzamy.
 */
export function tollsSentences(
  breakdown: ReadonlyArray<string> | undefined,
  items: ReadonlyArray<{ figure: RouteFigure | null | undefined; where?: string }>,
): string[] {
  if (breakdown?.some((line) => /opłat/i.test(line))) return [];
  const lines = items
    .map(({ figure, where }) => tollsText(figure, where))
    .filter((s): s is string => s !== null);
  // Obie trasy bez map — jedno „bez opłat drogowych”, nie dwa.
  if (lines.length > 1 && lines.every((s) => s.endsWith('bez opłat drogowych'))) {
    return ['Bez opłat drogowych.'];
  }
  return lines.map((s) => `${s.charAt(0).toUpperCase()}${s.slice(1)}.`);
}

/** Dotychczasowe zastrzeżenie marży — bez map HERE i w starszym API. */
export const MARGIN_ESTIMATE_CAVEAT = 'Szacunek: dolot ±8%, czas przejazdu z modelu floty.';

/**
 * Zastrzeżenie na końcu dymka marży. Gdy dolot jest z map, „±8%” byłoby
 * nieprawdą — mówimy, skąd są kilometry dolotu i trasy zlecenia.
 */
export function marginCaveat(
  access: RouteFigure | null | undefined,
  route: RouteFigure | null | undefined,
): string {
  if (access?.source !== 'here') return MARGIN_ESTIMATE_CAVEAT;
  const routePart =
    route?.source === 'here'
      ? 'dolot i trasa zlecenia z map HERE'
      : route?.source === 'tms'
        ? 'dolot z map HERE, trasa zlecenia z TMS'
        : route?.source === 'estimate'
          ? 'dolot z map HERE, trasa zlecenia szacunkowa (±8%)'
          : 'dolot z map HERE';
  return `Szacunek: ${routePart}, koszty wg średnich stawek spółki pojazdu.`;
}

/**
 * Zdanie o dolocie pod tabelą rekomendacji. `null` — żaden dolot nie jest
 * z map (HERE wyłączone albo starsze API): zostaje dotychczasowe zdanie
 * o szacunku. Przy części z map mówimy, jak rozpoznać które — podpisem
 * w komórce — bo nagłówek kolumny jest wtedy neutralny. Trasę zlecenia
 * (`routes`) wymieniamy razem z dolotem tylko wtedy, gdy każda jest z map.
 */
export function accessSourceNote(
  access: ReadonlyArray<RouteFigure | null | undefined>,
  routes: ReadonlyArray<RouteFigure | null | undefined> = [],
): string | null {
  const figures = access.filter((f): f is RouteFigure => !!f);
  const here = figures.filter((f) => f.source === 'here').length;
  if (here === 0) return null;
  if (here === figures.length) {
    const routesHere = routes.length > 0 && routes.every((r) => r?.source === 'here');
    const subject =
      routesHere
        ? 'Dolot i trasa zlecenia to trasy ciężarówki wyznaczone'
        : 'Dolot to trasa ciężarówki wyznaczona';
    return (
      `${subject} mapami HERE. Szacunek z linii prostej razy mnożnik ` +
      'terenowy (ok. ±8%) pokazujemy poglądowo w dymku obok liczby.'
    );
  }
  return (
    'Dolot podpisany „HERE” to trasa ciężarówki wyznaczona mapami HERE, a podpisany „szacunek” — ' +
    'odległość w linii prostej razy mnożnik terenowy (ok. ±8%); powód szacunku jest w dymku. ' +
    'Przy trasach z map ten sam szacunek pokazujemy poglądowo w dymku obok liczby.'
  );
}
